// src/components/Timeline/components/TimelineImageGallery.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Paper,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Card,
  CardMedia,
  CardActionArea,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Tooltip,
  Chip,
  Drawer,
  AppBar,
  Toolbar,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Skeleton
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon, 
  ArrowForward as ArrowForwardIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FullscreenExit as FullscreenExitIcon,
  Fullscreen as FullscreenIcon,
  Share as ShareIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Info as InfoIcon,
  Place as PlaceIcon,
  Label as LabelIcon,
  Person as PersonIcon,
  Comment as CommentIcon,
  Fullscreen,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import TimelinePhotoComments from './TimelinePhotoComments';

interface Image {
  id: string;
  url: string;
  title?: string;
  description?: string;
  location?: string;
  date?: string;
  tags?: string[];
  uploadedBy?: {
    id: string;
    name: string;
    avatar?: string;
  };
  width?: number;
  height?: number;
  tripId?: string;
}

interface TimelineImageGalleryProps {
  // Images can be either simple string URLs or more complex image objects
  images: (string | Image)[];
  initialIndex?: number;
  title?: string;
  onClose: () => void;
  onDelete?: (imageIndex: number) => void;
  onEdit?: (imageIndex: number) => void;
  isEditable?: boolean;
}

// Transition component for the dialog
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Fade ref={ref} {...props} />;
});

const TimelineImageGallery: React.FC<TimelineImageGalleryProps> = ({
  images,
  initialIndex = 0,
  title = "Photos",
  onClose,
  onDelete,
  onEdit,
  isEditable = false
}) => {

  
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [infoDrawerOpen, setInfoDrawerOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'single' | 'grid'>('single');
  const [showComments, setShowComments] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Process image data to ensure consistent format with better error handling
  const processedImages: Image[] = images
     .filter((image): image is string | Image => image !== undefined && image !== null)
     .map((image, index) => {
        if (typeof image === 'string') {
           return {
              id: `img-${index}`,
              url: image,
           };
        }

        // For Image objects, ensure url exists
        if (!image.url) {
           console.error('Image object missing URL:', image);
           return {
              id: image.id || `img-${index}`,
              url: 'https://via.placeholder.com/800x600?text=Image+Not+Available',
              title: image.title || 'Error: Missing Image URL',
           };
        }

        return image;
     });

  // Make sure we have valid images

  // Check if we have any valid images before setting current image
  const hasImages = processedImages.length > 0;
  const currentImage = hasImages ? processedImages[currentIndex] : null;
  


  // Fix the getCurrentImageUrl function to handle cases when currentImage might be null
  const getCurrentImageUrl = (): string => {
    if (!currentImage) {
      console.warn("No current image available");
      return 'https://via.placeholder.com/800x600?text=No+Image+Available';
    }
    

    return currentImage.url;
  };

  // Reset image loaded state when changing images
  useEffect(() => {
    setImageLoaded(false);
    setZoomLevel(1);
  }, [currentIndex]);
  
  // Navigate through images
  const handleNext = useCallback(() => {
    if (processedImages.length === 0) return;
    setCurrentIndex((prev) => (prev + 1) % processedImages.length);
  }, [processedImages.length]);
  
  const handlePrev = useCallback(() => {
    if (processedImages.length === 0) return;
    setCurrentIndex((prev) => (prev - 1 + processedImages.length) % processedImages.length);
  }, [processedImages.length]);
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
          handleNext();
          break;
        case 'ArrowLeft':
          handlePrev();
          break;
        case 'Escape':
          onClose();
          break;
        case '+':
          setZoomLevel(prev => Math.min(prev + 0.25, 3));
          break;
        case '-':
          setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
          break;
        case 'f':
          toggleFullscreen();
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleNext, handlePrev, onClose]);
  
  // Helper for zoom controls
  const zoomIn = () => setZoomLevel(prev => Math.min(prev + 0.25, 3));
  const zoomOut = () => setZoomLevel(prev => Math.max(prev - 0.25, 0.5));
  const resetZoom = () => setZoomLevel(1);
  
  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().then(() => {
          setIsFullscreen(false);
        }).catch(err => {
          console.error(`Error attempting to exit fullscreen: ${err.message}`);
        });
      }
    }
  };
  
  // Track fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);
  
  // Handle select image from grid
  const handleSelectImage = (index: number) => {
    setCurrentIndex(index);
    setViewMode('single');
  };
  
  // Helper to extract image ID
  const getImageId = (image: string | Image): string => {
    if (typeof image === 'string') {
      // Extract ID from URL if it's a string
      const parts = image.split('/');
      return parts[parts.length - 1].split('.')[0];
    }
    return image.id;
  };

  // Helper to extract trip ID
  const getTripId = (image: string | Image): string => {
    if (typeof image === 'string') {
      return ''; // Default empty string if we can't determine
    }
    return image.tripId || '';
  };
  
  // Check if property exists on image
  const hasProperty = (image: string | Image, property: keyof Image): boolean => {
    return typeof image !== 'string' && !!image[property];
  };

  // Get property value safely
  const getProperty = <K extends keyof Image>(image: string | Image, property: K): Image[K] | undefined => {
    if (typeof image === 'string') return undefined;
    return image[property];
  };

  // Render the single image view
  const renderSingleView = () => {
    if (!images || images.length === 0 || currentIndex >= images.length) return null;
    
    const currentImage = images[currentIndex];
    const imageUrl = typeof currentImage === 'string' ? currentImage : currentImage.url;
    const imageTitle = typeof currentImage === 'string' ? `Image ${currentIndex + 1}` : (currentImage.title || `Image ${currentIndex + 1}`);
    
    return (
      <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Top Toolbar */}
        <Box sx={{ 
          p: 2, 
          zIndex: 10, 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: 'rgba(0,0,0,0.5)',
          color: 'white'
        }}>
          <Typography variant="h6">
            {imageTitle}
          </Typography>
          <Box>
            <IconButton color="inherit" onClick={() => setShowComments(!showComments)}>
              <TimelinePhotoComments 
                photoId={getImageId(currentImage)}
                tripId={getTripId(currentImage)}
                compact={true}
              />
            </IconButton>
            <Tooltip title="View grid">
              <IconButton 
                color="inherit"
                onClick={() => setViewMode('grid')}
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
            {hasProperty(currentImage, 'location') || hasProperty(currentImage, 'tags') || hasProperty(currentImage, 'description') && (
              <Tooltip title="Image info">
                <IconButton 
                  color="inherit"
                  onClick={() => setInfoDrawerOpen(true)}
                >
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            )}
            
            {isEditable && (
              <>
                {onEdit && (
                  <Tooltip title="Edit image">
                    <IconButton 
                      size="small" 
                      onClick={() => onEdit(currentIndex)}
                      color="inherit"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                )}
                
                {onDelete && (
                  <Tooltip title="Delete image">
                    <IconButton 
                      size="small" 
                      onClick={() => onDelete(currentIndex)}
                      color="inherit"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}
          </Box>
        </Box>
        
        {/* Main image container */}
        <Box sx={{ 
          flexGrow: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'black',
          overflow: 'auto',
          position: 'relative'
        }}>
          {/* Loading indicator */}
          {!imageLoaded && (
            <Box 
              sx={{ 
                position: 'absolute', 
                top: '50%', 
                left: '50%', 
                transform: 'translate(-50%, -50%)',
                zIndex: 1
              }}
            >
              <CircularProgress color="secondary" />
            </Box>
          )}
          
          {/* Main image */}
          <img 
            src={imageUrl}
            alt={imageTitle}
            style={{ 
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              transform: `scale(${zoomLevel})`,
              transition: 'transform 0.2s ease-out',
              opacity: imageLoaded ? 1 : 0,
              cursor: zoomLevel !== 1 ? 'zoom-out' : 'default'
            }}
            onClick={zoomLevel !== 1 ? resetZoom : undefined}
            onLoad={() => setImageLoaded(true)}
            onError={(e) => {
              console.error('Error loading image:', e);
              setImageLoaded(true); // Still mark as loaded to remove the loading indicator
              // Replace with a placeholder
              (e.target as HTMLImageElement).src = 'https://via.placeholder.com/800x600?text=Image+Not+Available';
            }}
          />
        </Box>
        
        {/* Photo Information */}
        <Box sx={{ 
          p: 2, 
          backgroundColor: 'rgba(0,0,0,0.7)',
          color: 'white'
        }}>
          <List disablePadding>
            {hasProperty(currentImage, 'description') && (
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemAvatar>
                  <Avatar>
                    <InfoIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Description"
                  secondary={getProperty(currentImage, 'description')}
                />
              </ListItem>
            )}
            
            {hasProperty(currentImage, 'location') && (
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemAvatar>
                  <Avatar>
                    <PlaceIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Location"
                  secondary={getProperty(currentImage, 'location')}
                />
              </ListItem>
            )}
            
            {hasProperty(currentImage, 'date') && (
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemAvatar>
                  <Avatar>
                    <CommentIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Date"
                  secondary={getProperty(currentImage, 'date')}
                />
              </ListItem>
            )}
            
            {hasProperty(currentImage, 'uploadedBy') && (
              <ListItem sx={{ px: 0, py: 1 }}>
                <ListItemAvatar>
                  <Avatar src={getProperty(currentImage, 'uploadedBy')?.avatar}>
                    <PersonIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary="Uploaded by"
                  secondary={getProperty(currentImage, 'uploadedBy')?.name}
                />
              </ListItem>
            )}
          </List>
          
          {hasProperty(currentImage, 'tags') && getProperty(currentImage, 'tags')?.length && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Tags
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {getProperty(currentImage, 'tags')?.map((tag, i) => (
                  <Chip
                    key={i}
                    label={tag}
                    size="small"
                    icon={<LabelIcon fontSize="small" />}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
        
        {/* Comments Section */}
        {showComments && (
          <Box sx={{ 
            position: 'absolute',
            top: 0,
            right: 0,
            width: '350px',
            height: '100%',
            backgroundColor: 'background.paper',
            borderLeft: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.3s ease'
          }}>
            <Box sx={{ 
              p: 2, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}>
              <Typography variant="h6">Comments</Typography>
              <IconButton size="small" onClick={() => setShowComments(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ 
              p: 2, 
              overflowY: 'auto',
              flexGrow: 1
            }}>
              <TimelinePhotoComments 
                photoId={getImageId(currentImage)}
                tripId={getTripId(currentImage)}
              />
            </Box>
          </Box>
        )}
      </Box>
    );
  };
  
  // Render the grid view
  const renderGridView = () => (
    <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Grid container spacing={1}>
        {processedImages.map((image, index) => (
          <Grid item xs={6} sm={4} md={3} key={index}>
            <Card 
              elevation={currentIndex === index ? 8 : 1}
              sx={{ 
                position: 'relative',
                transition: 'all 0.2s',
                transform: currentIndex === index ? 'scale(1.05)' : 'scale(1)',
                zIndex: currentIndex === index ? 1 : 'auto',
                borderRadius: 1
              }}
            >
              <CardActionArea onClick={() => handleSelectImage(index)}>
                <CardMedia
                  component="img"
                  height={140}
                  image={image.url}
                  alt={image.title || `Image ${index + 1}`}
                  sx={{ 
                    objectFit: 'cover'
                  }}
                />
                {image.title && (
                  <Box 
                    sx={{ 
                      position: 'absolute', 
                      bottom: 0, 
                      left: 0, 
                      right: 0, 
                      bgcolor: 'rgba(0,0,0,0.5)',
                      p: 0.5
                    }}
                  >
                    <Typography variant="caption" color="white" noWrap>
                      {image.title}
                    </Typography>
                  </Box>
                )}
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
  
  // Image info drawer
  const renderImageInfoDrawer = () => (
    <Drawer
      anchor="right"
      open={infoDrawerOpen}
      onClose={() => setInfoDrawerOpen(false)}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 360 },
          maxWidth: '100%'
        }
      }}
    >
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            Image Information
          </Typography>
          <IconButton edge="end" onClick={() => setInfoDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      
      <Box sx={{ p: 2 }}>
        {currentImage?.title && (
          <Typography variant="h6" gutterBottom>
            {currentImage.title}
          </Typography>
        )}
        
        <List disablePadding>
          {currentImage?.description && (
            <ListItem sx={{ px: 0, py: 1 }}>
              <ListItemAvatar>
                <Avatar>
                  <InfoIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary="Description"
                secondary={currentImage.description}
              />
            </ListItem>
          )}
          
          {currentImage?.location && (
            <ListItem sx={{ px: 0, py: 1 }}>
              <ListItemAvatar>
                <Avatar>
                  <PlaceIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary="Location"
                secondary={currentImage.location}
              />
            </ListItem>
          )}
          
          {currentImage?.date && (
            <ListItem sx={{ px: 0, py: 1 }}>
              <ListItemAvatar>
                <Avatar>
                  <CommentIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary="Date"
                secondary={currentImage.date}
              />
            </ListItem>
          )}
          
          {currentImage?.uploadedBy && (
            <ListItem sx={{ px: 0, py: 1 }}>
              <ListItemAvatar>
                <Avatar src={currentImage.uploadedBy.avatar}>
                  <PersonIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary="Uploaded by"
                secondary={currentImage.uploadedBy.name}
              />
            </ListItem>
          )}
        </List>
        
        {currentImage?.tags && currentImage.tags.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Tags
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {currentImage.tags.map((tag, i) => (
                <Chip
                  key={i}
                  label={tag}
                  size="small"
                  icon={<LabelIcon fontSize="small" />}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
  
  return (
    <Dialog
      fullScreen
      open={true}
      onClose={onClose}
      TransitionComponent={Transition}
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper'
        }}
      >
        {/* Header */}
        <AppBar position="static" color="default" elevation={1}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={onClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" sx={{ ml: 2, flex: 1 }}>
              {title}
            </Typography>
            {viewMode === 'grid' && (
              <Button
                color="inherit"
                onClick={() => setViewMode('single')}
                startIcon={<ArrowBackIcon />}
              >
                Back to Slideshow
              </Button>
            )}
          </Toolbar>
        </AppBar>
        
        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {hasImages ? (
            viewMode === 'single' ? renderSingleView() : renderGridView()
          ) : (
            <Box 
              sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                p: 4
              }}
            >
              <Typography variant="h6" color="text.secondary" align="center" gutterBottom>
                No images available
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                There are no photos to display for this timeline item.
              </Typography>
            </Box>
          )}
        </Box>
        
        {/* Image info drawer */}
        {renderImageInfoDrawer()}
      </Box>
    </Dialog>
  );
};

export default TimelineImageGallery;